// Reharm LAYERS — a reharmonization is a named, swappable derivation over the
// tune's original harmony, and the original is always one switch away.
// (docs/reharm-plan.md §5; Phase 0 of the reharm plan.)
//
// The shape, and why:
//
//   Score.reharm = { base, layers, active }
//
// - `form[].harmony` STAYS the effective harmony — what plays and prints.
//   Every consumer (chartToSequence, toSong, export, analysis) keeps reading
//   it unchanged; a projection at the read seam would mean a dozen consumers
//   learning about layers.
// - `base` holds the ORIGINAL harmony of every measure ANY layer overrides,
//   captured on the first override — so "Original" is restored exactly,
//   never reconstructed.
// - A layer's `overrides` are WHOLE measure harmony arrays keyed by measure
//   id. Not per chord: onset-changing moves (a ii–V insertion, a Coltrane
//   cycle) replace one chord by several, chord ids are minted per array, and
//   a measure is the unit the passage selection and the sheet cell already
//   use. The cost is that a layer cannot add or remove bars.
// - Overrides are FLATTENED; `derivedFrom` is provenance only. Re-deriving B
//   from A copies A's overrides, so deleting A never breaks B.
// - It lives ON the Score (the `octaveShift` ruling): the undo stack pushes
//   whole Scores, so undoing a layer switch restores the form AND the active
//   pointer together, and loading another tune brings its own.
//
// The invariant, pinned in reharm.test.ts: for every measure m,
//   form[m].harmony  ==  active ? (overrides[m] ?? base[m] ?? form[m]) : (base[m] ?? form[m])
// `reconcileReharm` (run by normalizeScore on every commit) repairs a drifted
// blob in the SAFE direction — the form wins, because the form is what the
// user saw and edited; a reconcile must never revert an edit. Which is why the
// harmony ops also call `recordHarmonyEdit` explicitly: only the op knows the
// BEFORE state it needs to capture into `base` when a layer is active.

import { uid } from '../song';
import type { HarmonySymbol, Score } from './types';
// Type-only, and erased at build: the layer RECORDS what the engine did, the
// same way `provenance.moveId` records which move did it.
import type { AdventureBand } from '../reharm/surprisal';
import type { ReharmRung } from '../reharm/types';

/** The settings a passage derivation was made with. Recorded on the layer so
 *  selecting it can show them back — you can see what a layer IS, and carry on
 *  from there rather than guessing which dials produced it. Absent on a layer
 *  somebody built by hand. */
export interface ReharmDerivation {
  maxRung: ReharmRung;
  adventure: AdventureBand;
  density: number;
  variation: number;
}

export interface ReharmProvenance {
  /** Registry id of the move that produced this override ('tritone-sub'), or
   *  'manual' for a hand edit made while the layer was active. */
  moveId: string;
  rationale?: string;
}

export interface ReharmLayer {
  id: string;
  name: string;
  /** The layer this one was duplicated from — display provenance only. */
  derivedFrom: string | null;
  createdAt: number;
  /** The FULL harmony of every measure this layer changes, by FormMeasure.id.
   *  A measure not listed reads the original. */
  overrides: Record<string, HarmonySymbol[]>;
  /** Which move produced each override, for the sheet's marking + rationale. */
  provenance: Record<string, ReharmProvenance[]>;
  /** A layer being AUDITIONED: really applied, so the sheet draws it and the
   *  band plays it, but not yet kept. At most one exists at a time, it never
   *  survives a reload, and anything else writing the music discards it. */
  preview?: true;
  /** The settings that produced it, when it came from a passage derivation. */
  derivation?: ReharmDerivation;
}

export interface ReharmState {
  /** Original harmony of every measure any layer overrides. */
  base: Record<string, HarmonySymbol[]>;
  layers: ReharmLayer[];
  /** Active layer id; null = the Original. */
  active: string | null;
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

export function reharmLayers(score: Score): ReharmLayer[] {
  return score.reharm?.layers ?? [];
}

export function activeReharmLayer(score: Score): ReharmLayer | null {
  const state = score.reharm;
  if (!state || state.active === null) return null;
  return state.layers.find((layer) => layer.id === state.active) ?? null;
}

/** Whether `measureId`'s harmony differs from the original under the active layer. */
export function isMeasureReharmonized(score: Score, measureId: string): boolean {
  const layer = activeReharmLayer(score);
  return !!layer && measureId in layer.overrides;
}

/** The original harmony of a measure (what "Original" would show there). */
export function originalHarmony(score: Score, measureId: string): HarmonySymbol[] | null {
  const state = score.reharm;
  if (state && measureId in state.base) return state.base[measureId];
  const measure = score.form.find((m) => m.id === measureId);
  return measure ? measure.harmony : null;
}

/** What the effective harmony of `measureId` SHOULD be under `state`. */
function expectedHarmony(
  state: ReharmState,
  measureId: string,
  fallback: HarmonySymbol[],
): HarmonySymbol[] {
  const layer = state.active ? state.layers.find((l) => l.id === state.active) : undefined;
  if (layer && measureId in layer.overrides) return layer.overrides[measureId];
  if (measureId in state.base) return state.base[measureId];
  return fallback;
}

// ---------------------------------------------------------------------------
// Equality + cloning (chords are plain data; ids are part of the array)
// ---------------------------------------------------------------------------

export function sameHarmony(a: readonly HarmonySymbol[], b: readonly HarmonySymbol[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((chord, i) => JSON.stringify(chord) === JSON.stringify(b[i]));
}

const cloneChords = (chords: readonly HarmonySymbol[]): HarmonySymbol[] =>
  chords.map((chord) => ({
    ...chord,
    ...(chord.alternate ? { alternate: { ...chord.alternate } } : {}),
  }));

const cloneRecord = (record: Record<string, HarmonySymbol[]>): Record<string, HarmonySymbol[]> =>
  Object.fromEntries(Object.entries(record).map(([id, chords]) => [id, cloneChords(chords)]));

const cloneLayer = (layer: ReharmLayer): ReharmLayer => ({
  ...layer,
  overrides: cloneRecord(layer.overrides),
  provenance: Object.fromEntries(
    Object.entries(layer.provenance ?? {}).map(([id, list]) => [id, list.map((p) => ({ ...p }))]),
  ),
});

// ---------------------------------------------------------------------------
// Materialization
// ---------------------------------------------------------------------------

/** Rewrite the form's harmony to what `state` says it should be. */
function materialize(score: Score, state: ReharmState): Score {
  const touched = new Set<string>(Object.keys(state.base));
  for (const layer of state.layers) Object.keys(layer.overrides).forEach((id) => touched.add(id));
  return {
    ...score,
    reharm: state,
    form: score.form.map((measure) => {
      if (!touched.has(measure.id)) return measure;
      const expected = expectedHarmony(state, measure.id, measure.harmony);
      return sameHarmony(measure.harmony, expected)
        ? measure
        : { ...measure, harmony: cloneChords(expected) };
    }),
  };
}

/**
 * Bring `score.reharm` and `score.form` back into agreement, with the FORM
 * winning every disagreement. Run by normalizeScore on every commit. Also
 * prunes ids no longer in the form (a deleted measure leaves every layer) and
 * drops the whole state once no layers remain, so a plain score stays plain.
 * Returns the same object when nothing needs doing.
 */
export function reconcileReharm(score: Score): Score {
  const state = score.reharm;
  if (!state) return score;
  if (state.layers.length === 0) {
    const next = { ...score };
    delete next.reharm;
    return next;
  }
  const ids = new Set(score.form.map((m) => m.id));
  const harmonyOf = new Map(score.form.map((m) => [m.id, m.harmony]));
  let changed = false;

  const base: Record<string, HarmonySymbol[]> = {};
  for (const [id, chords] of Object.entries(state.base)) {
    if (!ids.has(id)) {
      changed = true;
      continue;
    }
    base[id] = chords;
  }
  const layers = state.layers.map((layer) => {
    let overrides = layer.overrides;
    let provenance = layer.provenance ?? {};
    for (const id of Object.keys(overrides)) {
      if (!ids.has(id)) {
        if (overrides === layer.overrides) overrides = { ...overrides };
        if (provenance === layer.provenance) provenance = { ...provenance };
        delete overrides[id];
        delete provenance[id];
        changed = true;
      }
    }
    if (!layer.provenance) changed = true;
    return overrides === layer.overrides && provenance === layer.provenance
      ? layer
      : { ...layer, overrides, provenance };
  });
  let active = state.active;
  if (active !== null && !layers.some((l) => l.id === active)) {
    active = null;
    changed = true;
  }

  // The form wins. With no layer active the form IS the original, so a base
  // entry that disagrees is stale and follows the form. With a layer active
  // the effective harmony belongs to that layer: a disagreement on a measure
  // the state already knows becomes (or updates) the layer's override; a
  // measure the state has never seen is untouched original and needs nothing.
  const activeIndex = active ? layers.findIndex((l) => l.id === active) : -1;
  let activeLayer = activeIndex >= 0 ? layers[activeIndex] : undefined;
  for (const [id, harmony] of harmonyOf) {
    if (!activeLayer) {
      if (id in base && !sameHarmony(base[id], harmony)) {
        base[id] = cloneChords(harmony);
        changed = true;
      }
      continue;
    }
    const known = id in activeLayer.overrides || id in base;
    if (!known) continue;
    const expected = expectedHarmony({ base, layers, active }, id, harmony);
    if (!sameHarmony(expected, harmony)) {
      activeLayer = {
        ...activeLayer,
        overrides: { ...activeLayer.overrides, [id]: cloneChords(harmony) },
      };
      changed = true;
    }
  }
  const finalLayers =
    activeLayer && activeLayer !== layers[activeIndex]
      ? layers.map((l, i) => (i === activeIndex ? activeLayer! : l))
      : layers;

  return changed ? { ...score, reharm: { base, layers: finalLayers, active } } : score;
}

// ---------------------------------------------------------------------------
// Recording harmony edits
// ---------------------------------------------------------------------------

/**
 * After a harmony op has produced `after` from `before`, file the change:
 * with a layer active it becomes that layer's override (capturing the
 * original into `base` first); with none active it is an edit to the
 * original, so `base` follows for any measure a layer overrides. Measures
 * whose harmony is unchanged are left alone. Cheap when the score has no
 * layers (returns `after` untouched), which is every score today.
 */
export function recordHarmonyEdit(
  before: Score,
  after: Score,
  provenance: ReharmProvenance = { moveId: 'manual' },
): Score {
  const state = after.reharm ?? before.reharm;
  if (!state || state.layers.length === 0) return after;
  const beforeHarmony = new Map(before.form.map((m) => [m.id, m.harmony]));
  const changedIds = after.form
    .filter((m) => {
      const prev = beforeHarmony.get(m.id);
      return prev === undefined || !sameHarmony(prev, m.harmony);
    })
    .map((m) => m.id);
  if (changedIds.length === 0) return after;

  const base = { ...state.base };
  let layers = state.layers;
  const activeIndex = state.active ? layers.findIndex((l) => l.id === state.active) : -1;
  if (activeIndex < 0) {
    for (const id of changedIds) {
      if (id in base) {
        const measure = after.form.find((m) => m.id === id)!;
        base[id] = cloneChords(measure.harmony);
      }
    }
    return { ...after, reharm: { ...state, base } };
  }
  const layer = cloneLayer(layers[activeIndex]);
  for (const id of changedIds) {
    if (!(id in base)) base[id] = cloneChords(beforeHarmony.get(id) ?? []);
    layer.overrides[id] = cloneChords(after.form.find((m) => m.id === id)!.harmony);
    layer.provenance[id] = [{ ...provenance }];
  }
  layers = layers.map((l, i) => (i === activeIndex ? layer : l));
  return { ...after, reharm: { ...state, base, layers } };
}

// ---------------------------------------------------------------------------
// Layer operations (each returns a NEW Score; null for a recognised no-op)
// ---------------------------------------------------------------------------

export function nextReharmLayerName(score: Score): string {
  const names = new Set(reharmLayers(score).map((l) => l.name));
  for (let n = 1; ; n++) {
    const candidate = `Reharm ${n}`;
    if (!names.has(candidate)) return candidate;
  }
}

/**
 * Create a layer and make it active. `from` names a layer to derive from
 * (its overrides are copied); omitted or null derives from the Original. A
 * fresh layer derived from the Original changes nothing about the form.
 */
export function createReharmLayer(
  score: Score,
  options: {
    name?: string;
    from?: string | null;
    now?: number;
    preview?: boolean;
    derivation?: ReharmDerivation;
  } = {},
): { score: Score; layerId: string } {
  const state: ReharmState = score.reharm ?? { base: {}, layers: [], active: null };
  const parent = options.from ? state.layers.find((l) => l.id === options.from) : undefined;
  const layer: ReharmLayer = {
    id: uid('reharm'),
    name: options.name?.trim() || nextReharmLayerName(score),
    derivedFrom: parent?.id ?? null,
    createdAt: options.now ?? Date.now(),
    overrides: parent ? cloneRecord(parent.overrides) : {},
    provenance: parent ? cloneLayer(parent).provenance : {},
    ...(options.preview ? { preview: true as const } : {}),
    // A layer duplicated from another inherits its settings, so "derive from
    // this" opens on the dials that made the thing you are deriving from.
    ...((options.derivation ?? parent?.derivation)
      ? { derivation: { ...(options.derivation ?? parent!.derivation!) } }
      : {}),
  };
  const next: ReharmState = {
    base: cloneRecord(state.base),
    layers: [...state.layers, layer],
    active: layer.id,
  };
  return { score: materialize(score, next), layerId: layer.id };
}

/** Switch the effective harmony to `layerId`, or to the Original with null. */
export function activateReharmLayer(score: Score, layerId: string | null): Score | null {
  const state = score.reharm;
  if (!state) return null;
  if (layerId !== null && !state.layers.some((l) => l.id === layerId)) return null;
  if (state.active === layerId) return null;
  return materialize(score, { ...state, active: layerId });
}

export function renameReharmLayer(score: Score, layerId: string, name: string): Score | null {
  const state = score.reharm;
  const trimmed = name.trim();
  if (!state || !trimmed) return null;
  const index = state.layers.findIndex((l) => l.id === layerId);
  if (index < 0 || state.layers[index].name === trimmed) return null;
  const layers = state.layers.map((l, i) => (i === index ? { ...l, name: trimmed } : l));
  return { ...score, reharm: { ...state, layers } };
}

/**
 * Delete a layer. Deleting the active one returns the form to the Original
 * first; deleting the last one drops the state so the score reads plain.
 */
export function deleteReharmLayer(score: Score, layerId: string): Score | null {
  const state = score.reharm;
  if (!state || !state.layers.some((l) => l.id === layerId)) return null;
  const layers = state.layers.filter((l) => l.id !== layerId);
  const active = state.active === layerId ? null : state.active;
  const next = materialize(score, { ...state, layers, active });
  if (layers.length > 0) return next;
  const plain = { ...next };
  delete plain.reharm;
  return plain;
}

/** Duplicate `layerId` as a new active layer ("derive from this"). */
export function duplicateReharmLayer(
  score: Score,
  layerId: string,
  name?: string,
): { score: Score; layerId: string } | null {
  const source = reharmLayers(score).find((l) => l.id === layerId);
  if (!source) return null;
  return createReharmLayer(score, { name: name?.trim() || `${source.name} copy`, from: layerId });
}

/**
 * In the active layer, give `measureIds` back their original harmony (drop
 * the overrides). Null when no layer is active or nothing was overridden.
 */
export function revertReharmMeasures(score: Score, measureIds: readonly string[]): Score | null {
  const state = score.reharm;
  const layer = activeReharmLayer(score);
  if (!state || !layer) return null;
  const targets = measureIds.filter((id) => id in layer.overrides);
  if (targets.length === 0) return null;
  const trimmed = cloneLayer(layer);
  for (const id of targets) {
    delete trimmed.overrides[id];
    delete trimmed.provenance[id];
  }
  const layers = state.layers.map((l) => (l.id === layer.id ? trimmed : l));
  return materialize(score, { ...state, layers });
}

/** Map every stored harmony array (base + every layer) through `fn` — the
 *  transposition hook. */
export function mapReharmHarmony(
  state: ReharmState,
  fn: (chords: HarmonySymbol[], measureId: string) => HarmonySymbol[],
): ReharmState {
  const mapRecord = (record: Record<string, HarmonySymbol[]>) =>
    Object.fromEntries(Object.entries(record).map(([id, chords]) => [id, fn(chords, id)]));
  return {
    ...state,
    base: mapRecord(state.base),
    layers: state.layers.map((layer) => ({ ...layer, overrides: mapRecord(layer.overrides) })),
  };
}

/** The invariant, as a predicate (tests + the validator). */
export function reharmInvariantHolds(score: Score): boolean {
  const state = score.reharm;
  if (!state) return true;
  return score.form.every((measure) =>
    sameHarmony(measure.harmony, expectedHarmony(state, measure.id, measure.harmony)),
  );
}

// ---------------------------------------------------------------------------
// The preview layer (audition)
// ---------------------------------------------------------------------------
// Trying a derivation should be something you HEAR and SEE before you decide
// to keep it, so the audition is a real layer: the form carries its harmony,
// the sheet marks its bars, the band plays it. What makes it an audition
// rather than an edit is that it is flagged, never persisted, and discarded
// by anything else that writes the music.

/** The layer currently being auditioned, if any. */
export function previewReharmLayer(score: Score): ReharmLayer | null {
  return reharmLayers(score).find((layer) => layer.preview) ?? null;
}

/**
 * Keep the audition: drop the preview flag so it becomes an ordinary layer,
 * under `name` (or the next free "Reharm N"). Null when nothing is being
 * auditioned. The harmony does not move — it was already applied.
 */
export function keepReharmPreview(score: Score, name?: string): Score | null {
  const state = score.reharm;
  const preview = previewReharmLayer(score);
  if (!state || !preview) return null;
  const layers = state.layers.map((layer) => {
    if (layer.id !== preview.id) return layer;
    const kept: ReharmLayer = { ...layer, name: name?.trim() || nextReharmLayerName(score) };
    delete kept.preview;
    return kept;
  });
  return { ...score, reharm: { ...state, layers } };
}

/**
 * Discard the audition, giving the form back the harmony it had underneath.
 * Returns the score UNCHANGED (same object) when nothing is being auditioned,
 * so the write seam that calls this on every musical write pays nothing.
 */
export function dropReharmPreview(score: Score): Score {
  const preview = previewReharmLayer(score);
  if (!preview) return score;
  const wasActive = score.reharm?.active === preview.id;
  const dropped = deleteReharmLayer(score, preview.id);
  if (!dropped) return score;
  // Back to the layer the audition was derived from, not to the Original —
  // auditioning over "Reharm 1" and then editing something else must not
  // quietly take Reharm 1 off the form too. Only while the audition was still
  // the active layer, though: a write that already chose one (a tap on
  // Original, another layer, New layer) ends the audition ON that choice.
  return wasActive ? (activateReharmLayer(dropped, preview.derivedFrom) ?? dropped) : dropped;
}
